import api from '@/libs/axiosInstance';
import type { ApiResponse, CurrentSubscription } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { shouldRetryDashboardQuery } from '@/libs/query-client';

/**
 * Fetch current subscription details
 *
 * @returns Current subscription data or null if no subscription exists
 * @throws Error if the request fails
 */
/**
 * Base key of the current-subscription query. Exported so every writer that
 * invalidates it (the Shopify welcome page, the cancel action, the success-fee
 * migration card) points at the same array instead of a literal that can drift.
 */
export const SUBSCRIPTION_QUERY_KEY = ['subscription'] as const;

const fetchSubscription = async (): Promise<CurrentSubscription | null> => {
  const response = await api.get<ApiResponse<CurrentSubscription | null>>(
    '/dashboard/subscription/current'
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fetch subscription');
  }

  return response.data.data ?? null;
};

interface UseSubscriptionQueryOptions {
  enabled?: boolean;
  scopeKey?: string | null;
}

/**
 * Hook to fetch current subscription details
 *
 * Features:
 * - Automatic caching with TanStack Query
 * - 2 minute stale time for responsive updates
 * - Refetches on window focus to stay in sync
 * - Returns null if no active subscription
 *
 * @param options - Query options including enabled flag
 * @returns Query result with subscription data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data: subscription, isLoading, error } = useSubscriptionQuery();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage />;
 * if (!subscription) return <NoSubscription />;
 *
 * return <SubscriptionDetails subscription={subscription} />;
 * ```
 */
export function useSubscriptionQuery(options: UseSubscriptionQueryOptions = {}) {
  const { enabled = true, scopeKey } = options;

  return useQuery({
    queryKey: scopeKey ? [...SUBSCRIPTION_QUERY_KEY, scopeKey] : SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchSubscription,
    staleTime: 1000 * 60 * 2, // 2 minutes - reduced for more responsive updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: (failureCount, error) => shouldRetryDashboardQuery(failureCount, error, 1),
    enabled,
  });
}
